/**
 * src/hooks/useChunkedRecorder.js — 5-minute chunked voice recorder
 *
 * For long recordings (>5min). Splits recording into 5-min chunks,
 * uploads each progressively via uploadChunk(), then calls
 * finalizeRecording() when stopped.
 *
 * States: idle | recording | uploading | processing | done | error
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { uploadChunk, finalizeRecording } from '../lib/api/voice-v2';
import { emitVoiceEvent } from '../lib/voiceEvents';
import { registerMic, releaseMic } from '../lib/voice/micRegistry';

const CHUNK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

export default function useChunkedRecorder({ contextType, contextId, contextHint }) {
  const [state, setState] = useState('idle');
  const [chunkCount, setChunkCount] = useState(0);
  const [errorMsg, setErrorMsg] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const chunkIndexRef = useRef(0);
  const recordingIdRef = useRef(null);
  const timerRef = useRef(null);
  const elapsedRef = useRef(null);

  // Release mic + timers on unmount so chunked recording doesn't leak a live
  // MediaStream when the user navigates away mid-record.
  useEffect(() => {
    return () => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      if (elapsedRef.current) { clearInterval(elapsedRef.current); elapsedRef.current = null; }
      try {
        const rec = mediaRecorderRef.current;
        if (rec && rec.state === 'recording') rec.stop();
      } catch { /* noop */ }
      if (streamRef.current) {
        try { releaseMic(streamRef.current); } catch { /* noop */ }
        streamRef.current = null;
      }
    };
  }, []);

  const getRecordingId = useCallback(() => {
    if (!recordingIdRef.current) {
      recordingIdRef.current = `chunked-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    }
    return recordingIdRef.current;
  }, []);

  const uploadSingleChunk = useCallback(async (blob, index) => {
    const reader = new FileReader();
    const base64 = await new Promise((resolve, reject) => {
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

    return uploadChunk({
      recordingId: getRecordingId(),
      chunkIndex: index,
      audio: base64,
      mimeType: 'audio/webm;codecs=opus',
      contextType,
      contextId,
    });
  }, [contextType, contextId, getRecordingId]);

  const flushChunk = useCallback(async (isFinal = false) => {
    if (chunksRef.current.length === 0) return;

    const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
    chunksRef.current = [];
    const index = chunkIndexRef.current++;
    setChunkCount(prev => prev + 1);

    try {
      await uploadSingleChunk(blob, index);
      emitVoiceEvent('voice:chunk_uploaded', {
        contextType, contextId, chunkIndex: index, isFinal,
      });
    } catch (err) {
      console.error('[chunkedRecorder] chunk upload error:', err);
      setErrorMsg(`Chunk ${index} upload failed: ${err.message}`);
    }
  }, [contextType, contextId, uploadSingleChunk]);

  const startRecording = useCallback(async () => {
    try {
      setErrorMsg(null);
      setState('recording');
      setChunkCount(0);
      setElapsed(0);
      chunkIndexRef.current = 0;
      recordingIdRef.current = null;
      chunksRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const recorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 48000,
      });
      mediaRecorderRef.current = recorder;
      // 2026-04-20 (session 3) — register for global sweep. Without this,
      // the 5-minute chunked recorder stream survived overlay close and kept
      // the red mic indicator on.
      registerMic(stream, recorder, `useChunkedRecorder:${contextType}:${contextId}`);

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.start(1000);

      emitVoiceEvent('voice:record_start', { contextType, contextId, mode: 'chunked' });

      timerRef.current = setInterval(() => {
        flushChunk(false);
      }, CHUNK_INTERVAL_MS);

      elapsedRef.current = setInterval(() => {
        setElapsed(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('[chunkedRecorder] start error:', err);
      setErrorMsg('Microphone access denied');
      setState('error');
      setTimeout(() => setState('idle'), 3000);
    }
  }, [contextType, contextId, flushChunk]);

  const stopRecording = useCallback(async () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (elapsedRef.current) { clearInterval(elapsedRef.current); elapsedRef.current = null; }

    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }

    await new Promise(r => setTimeout(r, 200));

    setState('uploading');
    const recordingId = getRecordingId();
    emitVoiceEvent('voice:record_stop', { contextType, contextId, recordingId, mode: 'chunked' });

    await flushChunk(true);

    setState('processing');
    try {
      const result = await finalizeRecording(recordingId);
      if (result?.status === 'ready' || result?.success) {
        setState('done');
        emitVoiceEvent('voice:upload_complete', {
          contextType, contextId, recordingId, voiceRecordId: result.voice_record_id,
        });
        setTimeout(() => setState('idle'), 3000);
      } else {
        setErrorMsg(result?.error || 'Processing failed');
        setState('error');
        emitVoiceEvent('voice:finalize_error', { contextType, contextId, recordingId, error: result?.error || 'Processing failed' });
        setTimeout(() => setState('idle'), 5000);
      }
    } catch (err) {
      console.error('[chunkedRecorder] finalize error:', err);
      setErrorMsg(err.message);
      setState('error');
      emitVoiceEvent('voice:finalize_error', { contextType, contextId, recordingId, error: err.message });
      setTimeout(() => setState('idle'), 5000);
    }
  }, [contextType, contextId, flushChunk, getRecordingId]);

  const toggle = useCallback(() => {
    if (state === 'idle') startRecording();
    else if (state === 'recording') stopRecording();
  }, [state, startRecording, stopRecording]);

  return { state, chunkCount, elapsed, errorMsg, toggle };
}
